import { X } from "lucide-react";
import * as React from "react";
import { cn } from "../../lib/cn";
import type { ComposerChip } from "./types";

/**
 * Internal: a single dismissible mode chip rendered inside `Composer.ChipBar`.
 *
 * Visual: ultra-quiet — neutral hairline ring, ink-2 text, small ink-3 dot
 * before the label (per v18 `.mode-chip`). Counter is monospace, X button is
 * 14×14 with hover affordance. Spring `animate-chip-in` on mount.
 *
 * Reduced-motion: handled globally via the keyframe rules in
 * `packages/ui/src/styles.css` `@media (prefers-reduced-motion: reduce)`.
 */
export interface ComposerChipItemProps {
  chip: ComposerChip;
  /** Click on chip body — used for clickable chips (e.g. Focus → Focus Full). */
  onClick?: (id: string) => void;
  /** Click on X — caller removes the chip from its state. */
  onDismiss?: (id: string) => void;
  /** Animation delay in ms — staggers multiple chips. */
  delayMs?: number;
  className?: string;
}

export const ComposerChipItem = React.forwardRef<HTMLSpanElement, ComposerChipItemProps>(
  ({ chip, onClick, onDismiss, delayMs = 0, className }, ref) => {
    const Icon = chip.icon;
    const isClickable = !!chip.clickable && !!onClick;
    const accent = chip.tone === "accent";
    return (
      <span
        ref={ref}
        className={cn(
          "inline-flex h-[20px] items-center gap-1 rounded-full pr-1 pl-1.5",
          "border font-medium text-[10.5px] leading-none",
          "animate-chip-in",
          // Tone: accent flags a primary active mode (e.g. structured Focus)
          // with a subtle ando-orange tint; neutral keeps the quiet hairline.
          accent
            ? "border-ando-orange/30 bg-ando-orange-soft/40 text-ink-1"
            : "border-hairline bg-transparent text-ink-2",
          className,
        )}
        style={delayMs ? { animationDelay: `${delayMs}ms` } : undefined}
        data-chip-id={chip.id}
      >
        {/* Visual anchor — June 11, 2026: il dot e l'icona NON
            coesistono. Quando il chip ha un glifo (Globe, Telescope,
            FileText…), il glifo È il bullet. Il dot resta solo come
            fallback per chip senza icon così non parte nudo
            dall'inizio della pill. Pre-fix erano entrambi visibili e
            l'utente leggeva "● ⏚ Esplora tutto" → due bullet
            consecutivi, rumorosi. */}
        {Icon ? (
          <Icon size={11} className={cn("shrink-0", accent ? "text-ando-orange" : "text-ink-2")} />
        ) : (
          <span
            aria-hidden="true"
            className={cn(
              "h-1 w-1 shrink-0 rounded-full",
              accent ? "bg-ando-orange" : "bg-ink-3",
            )}
          />
        )}
        {isClickable ? (
          <button
            type="button"
            onClick={() => onClick(chip.id)}
            className="bg-transparent text-inherit transition-colors hover:text-ink-1 focus-visible:outline-none"
          >
            {chip.label}
          </button>
        ) : (
          <span>{chip.label}</span>
        )}
        {typeof chip.count === "number" ? (
          <span className="font-mono text-[10px] text-ink-3 tabular-nums leading-none">
            {chip.count}
          </span>
        ) : null}
        {onDismiss ? (
          <button
            type="button"
            onClick={() => onDismiss(chip.id)}
            aria-label={`Disattiva ${chip.label}`}
            className={cn(
              "inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-ink-4",
              "transition-colors duration-150 ease-out",
              "hover:bg-black/[0.05] hover:text-ink-1",
              "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ando-orange/45",
            )}
          >
            <X size={8} strokeWidth={2.5} />
          </button>
        ) : null}
      </span>
    );
  },
);
ComposerChipItem.displayName = "Composer.ChipItem";
